import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import { format, parseISO } from "date-fns";
import { NumericFormat } from "react-number-format";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import "./AddInvoiceProcurement.css";
import "react-datepicker/dist/react-datepicker.css";
import axiosInstance from "../../../Config/axiosInstance";
import { useDropzone } from "react-dropzone";

registerLocale("ro", ro);
const animatedComponents = makeAnimated();

/*constante*/
const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const DatePickerPopper = ({ children }) => (
  <div className="datepicker-popper-container">{children}</div>
);

const MAX_FILE_SIZE = 100 * 1024 * 1024;

const INVOICE_FIELDS_TO_PRESERVE = [
  "invoice_without_vat",
  "total_invoice_with_vat",
  "invoice_without_vat_system",
  "total_invoice_with_vat_system",
  "supplier_vat",
  "currency",
  "exchange_rate",
  "invoice_number",
  "invoice_date",
  "invoice_date_display",
  "bill_maturity",
  "bill_maturity_display",
  "medium_alert",
  "critical_alert",
  "remarks",
  "month",
  "year",
  "payments_status",
];

//campuri care trebuie resetate la schimbarea contractului/PO
const RESET_FIELDS_ON_CHANGE = {
  cost_id: "",
  bill_maturity: "",
  bill_maturity_display: "",
  original_invoice: null,
  difference_without_vat: "",
  difference_with_vat: "",
  difference_without_vat_system: "",
  difference_with_vat_system: "",
  credit_note_without_vat: "",
  credit_note_with_vat: "",
  credit_note_without_vat_system: "",
  credit_note_with_vat_system: "",
  original_currency_abbreviation: "",
};

const BACKEND_CALCULATED_FIELDS = new Set([
  "invoice_date_display",
  "bill_maturity_display",
  "total_invoice_with_vat",
  "invoice_without_vat_system",
  "total_invoice_with_vat_system",
  "original_currency_abbreviation",
]);

const DIFFERENCE_FIELDS = [
  "difference_without_vat",
  "difference_with_vat",
  "difference_without_vat_system",
  "difference_with_vat_system",
];

/*utilitare*/
const getDefaultFormData = () => ({
  project_code: "",
  subproject_code: "",
  cost_id: "",
  supplier: "",
  invoice_number: "",
  invoice_date: "",
  bill_maturity: "",
  invoice_date_display: "",
  bill_maturity_display: "",
  invoice_without_vat: "",
  supplier_vat: "21",
  total_invoice_with_vat: "",
  currency: "",
  exchange_rate: "",
  invoice_without_vat_system: "",
  total_invoice_with_vat_system: "",
  medium_alert: "5",
  critical_alert: "1",
  remarks: "",
  month: "",
  year: "",
  payments_status: "Unpaid",
  contract: "",
  purchase_order: "",
  original_invoice: null,
  credit_note_without_vat: "",
  credit_note_with_vat: "",
  credit_note_without_vat_system: "",
  credit_note_with_vat_system: "",
  difference_without_vat: "",
  difference_with_vat: "",
  difference_without_vat_system: "",
  difference_with_vat_system: "",
  original_currency_abbreviation: "",
});

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const getDifferenceColorClass = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return "";
  return num < 0 ? "difference-field--negative" : "difference-field--positive";
};

/*componenete mici*/
const ErrorMessage = memo(({ error }) =>
  error ? (
    <div className="AddInvoiceProcurement-error">
      <span className="error-icon">⚠️</span>
      <span>{Array.isArray(error) ? error[0] : error}</span>
    </div>
  ) : null
);

const SectionHeader = memo(({ title, section, expanded, onToggle }) => (
  <div className="AddInvoiceProcurement-section-header" onClick={() => onToggle(section)}>
    <h2>{title}</h2>
    <button type="button" className="toggle-btn">
      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </button>
  </div>
));

const CalendarIcon = memo(() => (
  <span className="calendar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  </span>
));

const getFileIcon = (file) => {
  const t = file.type;
  if (t === "application/pdf")
    return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
  if (t === "application/msword" || t === "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
  if (t === "application/vnd.ms-excel" || t === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
    return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
  if (t.startsWith("image/"))
    return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
  return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
};

/*componenta principala*/
const AddInvoiceProcurement = () => {
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");
  const [projectCodes, setProjectCodes] = useState([]);
  const [subprojectCodes, setSubprojectCodes] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [costElements, setCostElements] = useState([]);
  const [monthChoices, setMonthChoices] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);
  const [selectedCostElement, setSelectedCostElement] = useState(null);
  const [selectedType, setSelectedType] = useState("contract");

  const [projectCodeDisplay, setProjectCodeDisplay] = useState("");
  const [supplierDisplay, setSupplierDisplay] = useState("");
  const [subprojectCodeDisplay, setSubprojectCodeDisplay] = useState("");
  const [errorNotification, setErrorNotification] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(Date.now());
  const [dropzoneKey, setDropzoneKey] = useState(Date.now());
  const [isCalculatingMaturity, setIsCalculatingMaturity] = useState(false);
  const [files, setFiles] = useState([]);
  const [showPOMessage, setShowPOMessage] = useState(false);
  const [poMessage, setPoMessage] = useState("");

  const [expandedSections, setExpandedSections] = useState({
    invoice: true,
    originalInvoice: true,
    amounts: true,
    differences: true,
    additionalDetails: true,
    attachments: true,
  });

  const [formData, setFormData] = useState(getDefaultFormData);

  //functii helper
  const showError = useCallback((message, timeout = 5000) => {
    setErrorNotification(message);
    setTimeout(() => setErrorNotification(""), timeout);
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const clearFieldError = useCallback((name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      return { ...prev, [name]: "" };
    });
  }, []);

  //matching project/subproject/supplier
  const matchEntityData = useCallback((entityData) => {
      const matchedProjectCode = projectCodes.find((pc) => pc.value === entityData.project_code);
      const matchedSubprojectCode = subprojectCodes.find((spc) => spc.value === entityData.subproject_code);
      const matchedSupplier = allSuppliers.find((s) => s.value === entityData.supplier);
      return {
        projectCodeDisplay: matchedProjectCode?.label || "",
        subprojectCodeDisplay: matchedSubprojectCode?.label || "",
        supplierDisplay: matchedSupplier?.label || "",
        project_code: entityData.project_code || "",
        subproject_code: entityData.subproject_code || "",
        supplier: entityData.supplier || "",
      };
    }, [projectCodes, subprojectCodes, allSuppliers]);

  const applyEntityDisplay = useCallback((match) => {
    setProjectCodeDisplay(match.projectCodeDisplay);
    setSubprojectCodeDisplay(match.subprojectCodeDisplay);
    setSupplierDisplay(match.supplierDisplay);
  }, []);

  const resetEntityDisplay = useCallback(() => {
    setProjectCodeDisplay("");
    setSubprojectCodeDisplay("");
    setSupplierDisplay("");
  }, []);

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const m = new Map();
    for (const r of exchangeRates) {
      const curId = Number(r.currency);
      if (!m.has(curId)) m.set(curId, []);
      m.get(curId).push({
        value: r.id,
        label: String(r.value ?? ""),
        dateLabel: r.date || "",
        rateValue: Number(r.value),
        rateDate: r.date,
      });
    }
    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      m.set(k, arr);
    }
    return m;
  }, [exchangeRates]);

  const isSystemCurrency = useMemo(() => {
    if (!formData.currency || !systemCurrencyId) return false;
    return Number(formData.currency) === Number(systemCurrencyId);
  }, [formData.currency, systemCurrencyId]);

  const exchangeOptions = useMemo(() => {
    if (!formData.currency) return [];
    return exchangeOptionsByCurrencyId.get(Number(formData.currency)) || [];
  }, [formData.currency, exchangeOptionsByCurrencyId]);

  const isInvoiceSelectDisabled = useMemo(() =>
    selectedType !== "credit_note" || (!formData.contract && !formData.purchase_order),
    [selectedType, formData.contract, formData.purchase_order]);

  const getInvoicePlaceholder = useMemo(() => {
    if (selectedType !== "credit_note") return "";
    if (!formData.contract && !formData.purchase_order)
      return "Select a Contract or Purchase Order first";
    if (allInvoices.length === 0)
      return "No invoices found for this Contract/PO";
    return "Select original invoice";
  }, [selectedType, formData.contract, formData.purchase_order, allInvoices.length]);

  const filteredCostElements = useMemo(() => {
    if (!formData.project_code || costElements.length === 0) return [];
    const selectedProject = projectCodes.find((pc) => pc.value === formData.project_code);
    const selectedProjectCodeLabel = selectedProject?.label.split(" - ")[0];
    return costElements.filter((ce) => ce.projectCode === selectedProjectCodeLabel);
  }, [formData.project_code, costElements, projectCodes]);

  const resolvedCostElement = useMemo(() => {
    if (!formData.cost_id) return null;
    return filteredCostElements.find((ce) => ce.value === formData.cost_id) || null;
  }, [formData.cost_id, filteredCostElements]);

  //calcule
  const calculateSystemAmounts = useCallback(
    (amount, currencyId, exchangeRateOption) => {
      const value = Number(amount);
      if (!currencyId || !systemCurrencyId || !Number.isFinite(value)) return "";
      if (Number(currencyId) === Number(systemCurrencyId)) return value.toFixed(2);
      const rateValue = Number(exchangeRateOption?.rateValue);
      if (!Number.isFinite(rateValue) || rateValue <= 0) return "";
      return (value * rateValue).toFixed(2);
    },[systemCurrencyId]);

  const calculateBillMaturityFromPO = useCallback(async (poId) => {
      if (!poId) return null;
      setIsCalculatingMaturity(true);
      try {
        const response = await axiosInstance.get(`/purchase-order/${poId}/calculate-bill-maturity/`);
        if (response.data?.bill_maturity) {
          const maturityDate = new Date(response.data.bill_maturity);
          return {
            isoDate: format(maturityDate, "yyyy-MM-dd"),
            displayDate: format(maturityDate, "dd.MM.yyyy"),
          };
        }
        return null;
      } catch (error) {
        console.error("Error calculating bill maturity:", error);
        if (error.response?.data?.error) showError(error.response.data.error);
        return null;
      } finally {
        setIsCalculatingMaturity(false);
      }
    }, [showError]);

  const loadOriginalInvoiceData = useCallback(async (invoiceId) => {
      if (!invoiceId) {
        setFormData((prev) => ({
          ...prev,
          invoice_without_vat: "",
          total_invoice_with_vat: "",
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
          original_currency_abbreviation: "",
        }));
        return;
      }
      try {
        const response = await axiosInstance.get(`/invoice-procurement/${invoiceId}/`);
        const invoice = response.data;
        const foundCurrency = currencies.find((c) => c.value === invoice.currency);
        const currencyAbbr = foundCurrency?.abbreviation || foundCurrency?.label || "";

        setFormData((prev) => ({
          ...prev,
          invoice_without_vat: invoice.invoice_without_vat || "",
          total_invoice_with_vat: invoice.total_invoice_with_vat || "",
          invoice_without_vat_system: invoice.invoice_without_vat_system || "",
          total_invoice_with_vat_system: invoice.total_invoice_with_vat_system || "",
          original_currency_abbreviation: currencyAbbr,
        }));
        setFieldErrors((prev) => ({ ...prev, original_invoice: "" }));
      } catch (error) {
        console.error("Error loading original invoice:", error);
        showError("Could not load original invoice data.");
      }
    }, [currencies, showError]);

  //fetch inital data
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [suppliersRes, projectCodesRes, subProjectCodesRes, contractsRes, purchaseOrdersRes, currenciesRes, exchangeRes, systemRes, monthChoicesRes,] = await Promise.all([
          axiosInstance.get("/suppliers/"),
          axiosInstance.get("/project-codes/"),
          axiosInstance.get("/subprojectcodes/"),
          axiosInstance.get("/contract-register/"),
          axiosInstance.get("/purchase-order/"),
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
          axiosInstance.get("/month-choices/"),
        ]);

        setAllSuppliers(
          (suppliersRes.data || []).map((s) => ({ value: s.id, label: s.name }))
        );
        setProjectCodes(
          (projectCodesRes.data || []).map((pc) => ({
            value: pc.id,
            label: `${pc.code} - ${pc.description}`,
          }))
        );
        setSubprojectCodes(
          (subProjectCodesRes.data || []).map((spc) => ({
            value: spc.id,
            label: `${spc.code} - ${spc.description}`,
          }))
        );
        setContracts(
          (contractsRes.data || []).map((c) => ({
            value: c.id,
            label: c.contract_number,
            contractData: c,
          }))
        );
        setPurchaseOrders(
          (purchaseOrdersRes.data || []).map((po) => ({
            value: po.id,
            label: po.order_number,
            poData: po,
          }))
        );

        const mappedCurrencies = (currenciesRes.data || []).map((curr) => ({
          value: curr.id,
          label: curr.abbreviation || curr.name,
          abbreviation: curr.abbreviation,
          name: curr.name,
        }));
        setCurrencies(mappedCurrencies);

        setExchangeRates((exchangeRes.data || []).sort((a, b) => new Date(b.date) - new Date(a.date)));

        if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
          const currencyId = systemRes.data[0]?.currency ?? null;
          setSystemCurrencyId(currencyId || null);
          const sys = mappedCurrencies.find((c) => Number(c.value) === Number(currencyId));
          setSystemCurrencyLabel(sys?.abbreviation || sys?.name || "");
        }

        if (monthChoicesRes.data?.month_choices) {
          setMonthChoices(
            monthChoicesRes.data.month_choices.map(([value, label]) => ({
              value,
              label,
            }))
          );
        }
      } catch (error) {
        console.error("Initial load error:", error);
        showError("Could not load form data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [showError]);

  useEffect(() => {
    const fetchCostElements = async () => {
      try {
        const [projectBudgetRes, budgetStructureRes] = await Promise.all([
          axiosInstance.get("/project-budget/"),
          axiosInstance.get("/budget-structure/"),
        ]);

        const materialCostElements = projectBudgetRes.data.filter((pb) => pb.source_type === "Material");
        const budgetStructures = budgetStructureRes.data;

        setCostElements(materialCostElements.map((ce) => {
            const matchedStructure = budgetStructures.find((bs) => bs.id === ce.cost_element);
            const number = matchedStructure?.cost_element_id || "N/A";
            const description = matchedStructure?.cost_element_description || "No description";
            const projectCode = matchedStructure?.project_code_details?.project_code || ce.project_code;
            return {
              value: ce.id,
              label: `${number} - ${description}`,
              projectCode,
              originalData: { costElement: ce, matchedStructure },
            };
          })
        );
      } catch (error) {
        console.error("Error fetching cost elements:", error);
      }
    };

    fetchCostElements();
  }, []);

  useEffect(() => {
    setSelectedCostElement(resolvedCostElement);
  }, [resolvedCostElement]);

  useEffect(() => {
    if (!formData.invoice_date) return;
    const date = parseISO(formData.invoice_date);
    const year = date.getFullYear().toString();
    const monthValue = String(date.getMonth() + 1).padStart(2, "0");
    setFormData((prev) => ({ ...prev, year, month: monthValue }));
    setFieldErrors((prev) => ({ ...prev, month: "", year: "" }));
  }, [formData.invoice_date]);

  useEffect(() => {
    if (selectedType !== "credit_note") {
      setAllInvoices([]);
      return;
    }

    const fetchInvoices = async () => {
      try {
        let filtered = [];
        if (formData.purchase_order) {
          const response = await axiosInstance.get(`/purchase-order/${formData.purchase_order}/invoice_numbers/`);
          const invoiceNumbers = response.data.invoice_numbers || [];
          if (invoiceNumbers.length === 0) {
            setAllInvoices([]);
            return;
          }
          const allInvoicesResponse = await axiosInstance.get("/invoice-procurement/");
          filtered = (allInvoicesResponse.data || []).filter((inv) => invoiceNumbers.includes(inv.invoice_number));
        } else if (formData.contract) {
          const response = await axiosInstance.get("/invoice-procurement/");
          filtered = (response.data || []).filter((inv) => inv.contract === formData.contract);
        }
        setAllInvoices(
          filtered.map((invoice) => ({
            value: invoice.id,
            label: `${invoice.invoice_number} - ${format(parseISO(invoice.invoice_date), "dd.MM.yyyy")}`,
          }))
        );
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setAllInvoices([]);
      }
    };

    fetchInvoices();
  }, [selectedType, formData.contract, formData.purchase_order]);

  useEffect(() => {
    if (selectedType === "credit_note" && formData.original_invoice) {
      loadOriginalInvoiceData(formData.original_invoice);
    }
  }, [loadOriginalInvoiceData, formData.original_invoice, selectedType]);

  useEffect(() => {
    if (selectedType !== "credit_note") {
      setFormData((prev) => {
        if (!prev.credit_note_with_vat && !prev.credit_note_without_vat_system && !prev.credit_note_with_vat_system && !prev.difference_without_vat && !prev.difference_with_vat)
          return prev;
        return {
          ...prev,
          credit_note_with_vat: "",
          credit_note_without_vat_system: "",
          credit_note_with_vat_system: "",
          difference_without_vat: "",
          difference_with_vat: "",
          difference_without_vat_system: "",
          difference_with_vat_system: "",
        };
      });
      return;
    }

    const creditWithoutVat = parseFloat(formData.credit_note_without_vat);
    const vatPct = parseFloat(formData.supplier_vat);
    const hasCreditValue = formData.credit_note_without_vat !== "" &&
      formData.credit_note_without_vat !== null &&
      formData.credit_note_without_vat !== undefined;
    const hasVat = formData.supplier_vat !== "" &&
      formData.supplier_vat !== null &&
      formData.supplier_vat !== undefined;

    if (hasCreditValue && hasVat && !isNaN(creditWithoutVat) && !isNaN(vatPct)) {
      const creditWithVat = creditWithoutVat * (1 + vatPct / 100);
      const creditWithoutVatSystem = calculateSystemAmounts(
        creditWithoutVat,
        formData.currency,
        selectedExchangeRate
      );
      const creditWithVatSystem = calculateSystemAmounts(
        creditWithVat,
        formData.currency,
        selectedExchangeRate
      );
      setFormData((prev) => ({
        ...prev,
        credit_note_with_vat: creditWithVat.toFixed(2),
        credit_note_without_vat_system: creditWithoutVatSystem,
        credit_note_with_vat_system: creditWithVatSystem,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        credit_note_with_vat: "",
        credit_note_without_vat_system: "",
        credit_note_with_vat_system: "",
      }));
    }
    const originalWithoutVat = parseFloat(formData.invoice_without_vat) || 0;
    const originalWithVat = parseFloat(formData.total_invoice_with_vat) || 0;
    const originalWithoutVatSystem = parseFloat(formData.invoice_without_vat_system) || 0;
    const originalWithVatSystem = parseFloat(formData.total_invoice_with_vat_system) || 0;
    const creditWithVat = parseFloat(formData.credit_note_with_vat) || 0;
    const creditWithoutVatSystem = parseFloat(formData.credit_note_without_vat_system) || 0;
    const creditWithVatSystem = parseFloat(formData.credit_note_with_vat_system) || 0;

    const hasOriginalData = formData.invoice_without_vat !== "" && formData.total_invoice_with_vat !== "";
    const hasCreditData = formData.credit_note_without_vat !== "" && formData.credit_note_with_vat !== "";

    if (!hasOriginalData || !hasCreditData) {
      setFormData((prev) => ({
        ...prev,
        difference_without_vat: "",
        difference_with_vat: "",
        difference_without_vat_system: "",
        difference_with_vat_system: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      difference_without_vat: (originalWithoutVat - (creditWithoutVat || 0)).toFixed(2),
      difference_with_vat: (originalWithVat - creditWithVat).toFixed(2),
      difference_without_vat_system: (originalWithoutVatSystem - creditWithoutVatSystem).toFixed(2),
      difference_with_vat_system: (originalWithVatSystem - creditWithVatSystem).toFixed(2),
    }));
  }, [ selectedType,
    formData.credit_note_without_vat,
    formData.supplier_vat,
    formData.currency,
    formData.invoice_without_vat,
    formData.total_invoice_with_vat,
    formData.invoice_without_vat_system,
    formData.total_invoice_with_vat_system,
    formData.credit_note_with_vat,
    formData.credit_note_without_vat_system,
    formData.credit_note_with_vat_system,
    selectedExchangeRate,
    calculateSystemAmounts,
  ]);

  useEffect(() => {
    if (selectedType === "credit_note") return;

    const invoiceWithoutVat = parseFloat(formData.invoice_without_vat);
    const vatPct = parseFloat(formData.supplier_vat);
    const hasInvoiceValue = formData.invoice_without_vat !== "" &&
      formData.invoice_without_vat !== null &&
      formData.invoice_without_vat !== undefined;
    const hasVat = formData.supplier_vat !== "" &&
      formData.supplier_vat !== null &&
      formData.supplier_vat !== undefined;

    if (hasInvoiceValue && hasVat && !isNaN(invoiceWithoutVat) && !isNaN(vatPct)) {
      const totalWithVat = invoiceWithoutVat * (1 + vatPct / 100);
      setFormData((prev) => ({...prev, total_invoice_with_vat: totalWithVat.toFixed(2), invoice_without_vat_system: calculateSystemAmounts(
          invoiceWithoutVat,
          formData.currency,
          selectedExchangeRate
        ),
        total_invoice_with_vat_system: calculateSystemAmounts(totalWithVat, formData.currency, selectedExchangeRate),
      }));
    }
  }, [
    formData.invoice_without_vat,
    formData.supplier_vat,
    formData.currency,
    selectedExchangeRate,
    calculateSystemAmounts,
    selectedType,
  ]);

  const handleTypeChange = (newType) => {
    if (newType === selectedType) return;

    const currentInvoiceValues = {};
    INVOICE_FIELDS_TO_PRESERVE.forEach((field) => {
      currentInvoiceValues[field] = formData[field];
    });

    const isContractOrPO = newType === "contract" || newType === "purchase_order";
    const wasContractOrPO = selectedType === "contract" || selectedType === "purchase_order";
    const shouldPreserveInvoiceValues = isContractOrPO && wasContractOrPO;

    let contractValue = formData.contract;
    let purchaseOrderValue = formData.purchase_order;
    let projectCodeValue = formData.project_code;
    let subprojectCodeValue = formData.subproject_code;
    let supplierValue = formData.supplier;
    let costIdValue = formData.cost_id;
    if ((newType === "contract" || newType === "credit_note") && contractValue) {
      const contractOption = contracts.find((c) => c.value === contractValue);
      if (contractOption) {
        setSelectedContract(contractOption);
        const match = matchEntityData(contractOption.contractData);
        applyEntityDisplay(match);
        projectCodeValue = match.project_code;
        subprojectCodeValue = match.subproject_code;
        supplierValue = match.supplier;
      } else if (newType === "contract") {
        resetEntityDisplay();
        contractValue = "";
        projectCodeValue = "";
        subprojectCodeValue = "";
        supplierValue = "";
      }
    } else if (newType === "contract") {
      setSelectedContract(null);
      resetEntityDisplay();
    }

    if ((newType === "purchase_order" || newType === "credit_note") && purchaseOrderValue) {
      const poOption = purchaseOrders.find((po) => po.value === purchaseOrderValue);
      if (poOption) {
        setSelectedPurchaseOrder(poOption);
        const match = matchEntityData(poOption.poData);
        applyEntityDisplay(match);
        projectCodeValue = match.project_code;
        subprojectCodeValue = match.subproject_code;
        supplierValue = match.supplier;
      } else if (newType === "purchase_order") {
        resetEntityDisplay();
        purchaseOrderValue = "";
        projectCodeValue = "";
        subprojectCodeValue = "";
        supplierValue = "";
      }
    } else if (newType === "purchase_order") {
      setSelectedPurchaseOrder(null);
      resetEntityDisplay();
    }

    if (newType === "contract") {
      purchaseOrderValue = "";
      setSelectedPurchaseOrder(null);
    } else if (newType === "purchase_order") {
      contractValue = "";
      setSelectedContract(null);
    }

    setSelectedCurrency( formData.currency ? currencies.find((c) => c.value === formData.currency) || null : null);
    setSelectedExchangeRate( formData.exchange_rate ? exchangeOptionsByCurrencyId.get(Number(formData.currency))
            ?.find((r) => r.value === Number(formData.exchange_rate)) || null
        : null
    );

    if (costIdValue && !filteredCostElements.find((ce) => ce.value === costIdValue)) {
      setSelectedCostElement(null);
      costIdValue = "";
    } else if (!costIdValue) {
      setSelectedCostElement(null);
    }

    const newFormData = {
      ...getDefaultFormData(),
      ...currentInvoiceValues,
      contract: contractValue || "",
      purchase_order: purchaseOrderValue || "",
      project_code: projectCodeValue || "",
      subproject_code: subprojectCodeValue || "",
      supplier: supplierValue || "",
      cost_id: costIdValue || "",
    };

    if (!shouldPreserveInvoiceValues) {
      newFormData.invoice_without_vat = "";
      newFormData.total_invoice_with_vat = "";
      newFormData.invoice_without_vat_system = "";
      newFormData.total_invoice_with_vat_system = "";
    }

    if (newType === "credit_note") {
      newFormData.credit_note_without_vat = formData.credit_note_without_vat || "";
      newFormData.credit_note_with_vat = formData.credit_note_with_vat || "";
      newFormData.credit_note_without_vat_system = formData.credit_note_without_vat_system || "";
      newFormData.credit_note_with_vat_system = formData.credit_note_with_vat_system || "";
      newFormData.original_invoice = formData.original_invoice || null;
      newFormData.original_currency_abbreviation = formData.original_currency_abbreviation || "";
    }

    setFormData(newFormData);
    setSelectedType(newType);
    setFieldErrors({});
  };

  const handleContractChange = useCallback((selectedOption) => {
      setSelectedContract(selectedOption);
      setSelectedPurchaseOrder(null);

      if (selectedOption) {
        const match = matchEntityData(selectedOption.contractData);
        applyEntityDisplay(match);
        setFormData((prev) => ({
          ...prev,
          contract: selectedOption.value,
          purchase_order: "",
          project_code: match.project_code,
          subproject_code: match.subproject_code,
          supplier: match.supplier,
          ...RESET_FIELDS_ON_CHANGE,
        }));
      } else {
        resetEntityDisplay();
        setFormData((prev) => ({
          ...prev,
          contract: "",
          project_code: "",
          subproject_code: "",
          supplier: "",
          ...RESET_FIELDS_ON_CHANGE,
          invoice_without_vat: "",
          total_invoice_with_vat: "",
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
        }));
      }
      setSelectedCostElement(null);
      clearFieldError("contract");
    },[matchEntityData, applyEntityDisplay, resetEntityDisplay, clearFieldError]);

  const handlePurchaseOrderChange = useCallback(async (selectedOption) => {
      setSelectedPurchaseOrder(selectedOption);
      setSelectedContract(null);

      if (selectedOption) {
        const match = matchEntityData(selectedOption.poData);
        applyEntityDisplay(match);
        setFormData((prev) => ({
          ...prev,
          purchase_order: selectedOption.value,
          contract: "",
          project_code: match.project_code,
          subproject_code: match.subproject_code,
          supplier: match.supplier,
          ...RESET_FIELDS_ON_CHANGE,
        }));

        const result = await calculateBillMaturityFromPO(selectedOption.value);
        if (result) {
          setFormData((prev) => ({
            ...prev,
            bill_maturity: result.isoDate,
            bill_maturity_display: result.displayDate,
          }));
          clearFieldError("bill_maturity");
        }
      } else {
        resetEntityDisplay();
        setFormData((prev) => ({
          ...prev,
          purchase_order: "",
          project_code: "",
          subproject_code: "",
          supplier: "",
          ...RESET_FIELDS_ON_CHANGE,
          invoice_without_vat: "",
          total_invoice_with_vat: "",
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
        }));
      }
      setSelectedCostElement(null);
      clearFieldError("purchase_order");
    }, [matchEntityData, applyEntityDisplay, resetEntityDisplay, calculateBillMaturityFromPO, clearFieldError,]);

  const handleOriginalInvoiceChange = useCallback((selectedOption) => {
      if (selectedOption) {
        setFormData((prev) => ({...prev, original_invoice: selectedOption.value,}));
        loadOriginalInvoiceData(selectedOption.value);
      } else {
        setFormData((prev) => ({
          ...prev,
          original_invoice: null,
          invoice_without_vat: "",
          total_invoice_with_vat: "",
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
          original_currency_abbreviation: "",
          ...DIFFERENCE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: "" }), {}),
        }));
      }
      clearFieldError("original_invoice");
    },[loadOriginalInvoiceData, clearFieldError]);

  const handleCostElementChange = useCallback((selectedOption) => {
      setSelectedCostElement(selectedOption);
      setFormData((prev) => ({...prev, cost_id: selectedOption ? selectedOption.value : "",}));
      clearFieldError("cost_id");
    }, [clearFieldError]);

  const handleCurrencyChange = useCallback((selectedOption) => {
      setSelectedCurrency(selectedOption);
      if (!selectedOption) {
        setSelectedExchangeRate(null);
        setFormData((prev) => ({
          ...prev,
          currency: "",
          exchange_rate: "",
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
          credit_note_without_vat_system: "",
          credit_note_with_vat_system: "",
        }));
      } else if (systemCurrencyId && Number(selectedOption.value) === Number(systemCurrencyId)) {
        setSelectedExchangeRate(null);
        setFormData((prev) => ({
          ...prev,
          currency: selectedOption.value,
          exchange_rate: "",
        }));
      } else {
        const optionsForCurrency = exchangeOptionsByCurrencyId.get(Number(selectedOption.value)) || [];
        const latestRate = optionsForCurrency[0] || null;
        setSelectedExchangeRate(latestRate);
        setFormData((prev) => ({
          ...prev,
          currency: selectedOption.value,
          exchange_rate: latestRate ? latestRate.value : "",
        }));
      }
      setFieldErrors((prev) => ({ ...prev, currency: "", exchange_rate: "" }));
    }, [systemCurrencyId, exchangeOptionsByCurrencyId]);

  const handleExchangeRateChange = useCallback((selectedOption) => {
      setSelectedExchangeRate(selectedOption);
      setFormData((prev) => ({
        ...prev,
        exchange_rate: selectedOption ? selectedOption.value : "",
      }));
      clearFieldError("exchange_rate");
    },[clearFieldError]);

  const handleChange = useCallback((e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      clearFieldError(name);
    }, [clearFieldError]);

  const handleDatePick = useCallback((field, date) => {
    if (!date) {
      setFormData((prev) => ({
        ...prev,
        [field]: "",
        [field + "_display"]: "",
      }));
      if (field === "bill_maturity") setDatePickerKey(Date.now());
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: format(date, "yyyy-MM-dd"),
      [field + "_display"]: format(date, "dd.MM.yyyy"),
    }));
  }, []);

  //dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      "image/jpeg": [],
      "image/png": [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/plain": [],
    },
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    onDrop: (acceptedFiles, rejectedFiles) => {
      const tooLarge = rejectedFiles.filter((f) => f.errors[0]?.code === "file-too-large");
      if (tooLarge.length > 0) {
        showError(`File ${tooLarge[0].file.name} exceeds 100MB`, 3000);
      }
      if (acceptedFiles.length > 0) {
        setFiles((prev) => [...prev, ...acceptedFiles]);
      }
    },
  });

  const handleRemoveFile = useCallback((indexToRemove) => {
    setFiles((prev) => {
      const updated = prev.filter((_, index) => index !== indexToRemove);
      if (updated.length === 0) setDropzoneKey(Date.now());
      return updated;
    });
  }, []);

  const handlePreviewFile = useCallback((file) => {
    const url = URL.createObjectURL(file);
    if (file.type === "application/pdf") {
      window.open(url, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }, []);

  //validare
  const validateFiles = useCallback(() => {
    if (files.length === 0) return true;
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      showError(`Following files exceed 100MB limit:\n${oversized.map((f) => f.name).join("\n")}`, 5000);
      return false;
    }
    return true;
  }, [files, showError]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.project_code) newErrors.project_code = "Required";
    if (!formData.supplier) newErrors.supplier = "Required";
    if (!formData.invoice_number) newErrors.invoice_number = "Required";
    if (!formData.invoice_date) newErrors.invoice_date = "Required";
    if (!formData.bill_maturity) newErrors.bill_maturity = "Required";
    if (!formData.currency) newErrors.currency = "Required";
    if (!isSystemCurrency && !formData.exchange_rate) newErrors.exchange_rate = "Exchange rate required";
    if (!formData.month) newErrors.month = "Required";
    if (!formData.year) newErrors.year = "Required";
    if (!formData.medium_alert) newErrors.medium_alert = "Required";
    if (!formData.critical_alert) newErrors.critical_alert = "Required";

    if (formData.supplier_vat === "" || formData.supplier_vat == null) newErrors.supplier_vat = "Required";

    if (selectedType !== "credit_note") {
      if (!formData.invoice_without_vat) newErrors.invoice_without_vat = "Required";
      if (!formData.total_invoice_with_vat || String(formData.total_invoice_with_vat).trim() === "")
        newErrors.total_invoice_with_vat = "Required";
    }

    if (selectedType === "contract" && !formData.contract) newErrors.contract = "Required";
    if (selectedType === "purchase_order" && !formData.purchase_order) newErrors.purchase_order = "Required";

    if (selectedType === "credit_note") {
      if (!formData.contract && !formData.purchase_order) {
        newErrors.contract = "Contract or Purchase Order is required";
        newErrors.purchase_order = "Contract or Purchase Order is required";
      }
      if (!formData.original_invoice) newErrors.original_invoice = "Required";
      if (!formData.credit_note_without_vat) newErrors.credit_note_without_vat = "Required";
      if (!formData.credit_note_with_vat) newErrors.credit_note_with_vat = "Required";
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isSystemCurrency, selectedType]);

  //submit
  const buildSubmissionData = useCallback(() => {
    const submissionData = new FormData();
    const shouldExcludeDifferences = selectedType !== "credit_note";

    Object.keys(formData).forEach((key) => {
      if (BACKEND_CALCULATED_FIELDS.has(key)) return;
      if (key.startsWith("difference_")) {
        if (shouldExcludeDifferences) return;
        const val = formData[key];
        if (val !== "" && val != null) submissionData.append(key, val);
        return;
      }
      const val = formData[key];
      if (val !== null && val !== undefined && val !== "") {
        submissionData.append(key, val);
      }
    });

    files.forEach((file) => submissionData.append("attachments", file));
    return submissionData;
  }, [formData, files, selectedType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaving) return;
    const filesValid = validateFiles();
    const formValid = validateForm();

    if (!filesValid || !formValid) {
      return;
    }

    if (String(selectedType).trim() === "credit_note" && formData.purchase_order) {
      const selectedPO = purchaseOrders.find((po) => String(po.value).trim() === String(formData.purchase_order).trim());
      const poNumber = selectedPO?.label || formData.purchase_order;
      setPoMessage(`Attention! Purchase Order ${poNumber} needs to be updated according to the invoice.`);
      setShowPOMessage(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setShowPOMessage(false);
    }

    setIsSaving(true);

    try {
      const submissionData = buildSubmissionData();
      await axiosInstance.post("/invoice-procurement/", submissionData,{
          headers: { "Content-Type": "multipart/form-data" },
        });
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      const backendErrors = error.response?.data;
      if (backendErrors) {
        const newErrors = {};
        if (backendErrors.invoice_number) {
          newErrors.invoice_number = ["This number already exists.",];
        }
        ["currency", "exchange_rate", "credit_note_without_vat", "credit_note_with_vat"].forEach((field) => {
          if (backendErrors[field]) {
            newErrors[field] = Array.isArray(backendErrors[field]) ? backendErrors[field][0] : backendErrors[field];
          }
        });
        if (backendErrors.non_field_errors) {
          showError(
            Array.isArray(backendErrors.non_field_errors) ? backendErrors.non_field_errors[0] : backendErrors.non_field_errors
          );
        }
        if (backendErrors.attachments) {
          showError("Error uploading files. Check file type and size.");
        }
        setFieldErrors(newErrors);
      } else {
        showError("An unexpected error occurred while saving the invoice.");
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  //render
  if (isLoading) {
    return (
      <div className="AddInvoiceProcurement">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading invoice form...</p>
        </div>
      </div>
    );
  }

  const isCreditNote = selectedType === "credit_note";

  return (
    <div className="AddInvoiceProcurement">
      {errorNotification && (
        <div className="AddInvoiceProcurement-top-error">{errorNotification}</div>
      )}

      {showToast &&
        createPortal(
          <div className="global-toast-on-top">✔ Invoice added successfully!</div>,
          document.body
        )}

      {showPOMessage && createPortal(
          <div className="AddInvoiceProcurement">
            <div className="po-warning-overlay">
              <div className="po-warning-modal">
                <div className="po-warning-modal-icon">
                  ⚠️
                </div>

                <div className="po-warning-modal-content">
                  <h2>Attention</h2>
                  <p>{poMessage}</p>
                </div>
              </div>
            </div>
          </div>,
          document.body
      )}

      <div className="AddInvoiceProcurement-header">
        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
        <h1 className="header-title">ADD PROCUREMENT INVOICE</h1>
        <div className="invoice-number-pill">
          Number: {formData.invoice_number || "-"}
        </div>
      </div>

      <hr className="title-separator" />

      <form onSubmit={handleSubmit} className="AddInvoiceProcurement-form">
        {/* SECTION 1: INVOICE DETAILS */}
        <div className="AddInvoiceProcurement-section">
          <SectionHeader title="INVOICE DETAILS" section="invoice" expanded={expandedSections.invoice} onToggle={toggleSection}/>

          {expandedSections.invoice && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group full-width">
                  <div className="radio-group">
                    {["contract", "purchase_order", "credit_note"].map((type) => (
                      <label key={type}>
                        <input
                          type="radio"
                          name="doc_type"
                          value={type}
                          checked={selectedType === type}
                          onChange={() => handleTypeChange(type)}
                        />
                        {type === "contract" ? "Contract"
                          : type === "purchase_order" ? "Purchase Order"
                          : "Credit Note"}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {selectedType === "contract" && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Contract <span className="required">*</span>
                    </label>
                    <Select
                      options={contracts}
                      classNamePrefix="AddInvoiceProcurement-select"
                      value={selectedContract}
                      onChange={handleContractChange}
                      placeholder="Select contract"
                      components={animatedComponents}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectPortalStyles}
                    />
                    <ErrorMessage error={fieldErrors.contract} />
                  </div>
                </div>
              )}

              {selectedType === "purchase_order" && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Purchase Order <span className="required">*</span>
                    </label>
                    <Select
                      options={purchaseOrders}
                      classNamePrefix="AddInvoiceProcurement-select"
                      value={selectedPurchaseOrder}
                      onChange={handlePurchaseOrderChange}
                      placeholder="Select purchase order"
                      components={animatedComponents}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectPortalStyles}
                    />
                    <ErrorMessage error={fieldErrors.purchase_order} />
                  </div>
                </div>
              )}

              {isCreditNote && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Contract</label>
                    <Select
                      options={contracts}
                      classNamePrefix="AddInvoiceProcurement-select"
                      value={selectedContract}
                      onChange={handleContractChange}
                      placeholder="Select contract"
                      components={animatedComponents}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectPortalStyles}
                    />
                    <ErrorMessage error={fieldErrors.contract} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Purchase Order</label>
                    <Select
                      options={purchaseOrders}
                      classNamePrefix="AddInvoiceProcurement-select"
                      value={selectedPurchaseOrder}
                      onChange={handlePurchaseOrderChange}
                      placeholder="Select purchase order"
                      components={animatedComponents}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectPortalStyles}
                    />
                    <ErrorMessage error={fieldErrors.purchase_order} />
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Project Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={projectCodeDisplay}
                    className="readonly-field"
                    readOnly
                  />
                  <ErrorMessage error={fieldErrors.project_code} />
                </div>

                <div className="form-group">
                  <label className="form-label">Subproject Code</label>
                  <input
                    type="text"
                    value={subprojectCodeDisplay}
                    className="readonly-field"
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Cost ID</label>
                  <Select
                    options={filteredCostElements}
                    classNamePrefix="AddInvoiceProcurement-select"
                    value={selectedCostElement}
                    onChange={handleCostElementChange}
                    placeholder={filteredCostElements.length ? "Select cost element" : "Select a project first" }
                    components={animatedComponents}
                    isClearable
                    isDisabled={!formData.project_code}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.cost_id} />
                </div>
              </div>

              <div className="form-row three-columns">
                <div className="form-group">
                  <label className="form-label">
                    Supplier <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={supplierDisplay}
                    className="readonly-field"
                    readOnly
                  />
                  <ErrorMessage error={fieldErrors.supplier} />
                </div>

                <div className="form-group">
                  <label className="form-label">Invoice Number <span className="required">*</span></label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleChange}
                    className="AddInvoiceProcurement-input"
                    autoComplete="off"
                  />
                  <ErrorMessage error={fieldErrors.invoice_number} />
                </div>

                <div className="form-group">
                  <label className="form-label">Invoice Date <span className="required">*</span></label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={formData.invoice_date ? parseISO(formData.invoice_date) : null}
                      onChange={(date) => {
                        handleDatePick("invoice_date", date);
                        clearFieldError("invoice_date");
                      }}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="dd.mm.yyyy"
                      className="AddInvoiceProcurement-date-input"
                      popperContainer={DatePickerPopper}
                    />
                    <CalendarIcon />
                  </div>
                  <ErrorMessage error={fieldErrors.invoice_date} />
                </div>
              </div>

              <div className="form-row three-columns">
                <div className="form-group">
                  <label className="form-label">Bill Maturity <span className="required">*</span></label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      key={datePickerKey}
                      selected={formData.bill_maturity ? parseISO(formData.bill_maturity) : null}
                      onChange={(date) => {
                        handleDatePick("bill_maturity", date);
                        clearFieldError("bill_maturity");
                      }}
                      dateFormat="dd.MM.yyyy"
                      placeholderText={isCalculatingMaturity ? "Calculating..." : "dd.mm.yyyy"}
                      className="AddInvoiceProcurement-date-input"
                      disabled={isCalculatingMaturity}
                      popperContainer={DatePickerPopper}
                    />
                    <CalendarIcon />
                  </div>
                  <ErrorMessage error={fieldErrors.bill_maturity} />
                </div>

                <div className="form-group">
                  <label className="form-label">Currency <span className="required">*</span></label>
                  <Select
                    options={currencies}
                    classNamePrefix="AddInvoiceProcurement-select"
                    value={selectedCurrency}
                    onChange={handleCurrencyChange}
                    placeholder="Select currency"
                    components={animatedComponents}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.currency} />
                </div>

                <div className="form-group">
                  <label className="form-label">Exchange Rate{" "} {!isSystemCurrency && <span className="required">*</span>}</label>
                  <Select
                    options={exchangeOptions}
                    classNamePrefix="AddInvoiceProcurement-select"
                    value={selectedExchangeRate}
                    onChange={handleExchangeRateChange}
                    placeholder={
                      !formData.currency
                        ? "Select currency first"
                        : isSystemCurrency
                        ? "Not needed"
                        : exchangeOptions.length
                        ? "Latest rate selected automatically"
                        : "No rates"
                    }
                    components={animatedComponents}
                    isClearable
                    isDisabled={!formData.currency || isSystemCurrency}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    formatOptionLabel={(option, { context }) =>
                      context === "value"
                        ? option.label
                        : `${option.label}${option.dateLabel ? ` (${formatDateLabel(option.dateLabel)})` : ""}`
                    }
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.exchange_rate} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SECTION 2: ORIGINAL INVOICE */}
        {isCreditNote && (
          <div className="AddInvoiceProcurement-section">
            <SectionHeader title="ORIGINAL INVOICE" section="originalInvoice" expanded={expandedSections.originalInvoice} onToggle={toggleSection} />

            {expandedSections.originalInvoice && (
              <div className="section-content">
                {(formData.contract || formData.purchase_order) && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Select Original Invoice{" "}<span className="required">*</span></label>
                        <Select
                          options={allInvoices}
                          classNamePrefix="AddInvoiceProcurement-select"
                          value={allInvoices.find((inv) => inv.value === formData.original_invoice) || null }
                          onChange={handleOriginalInvoiceChange}
                          placeholder={getInvoicePlaceholder}
                          components={animatedComponents}
                          isClearable
                          isDisabled={isInvoiceSelectDisabled}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={selectPortalStyles}
                        />
                        <ErrorMessage error={fieldErrors.original_invoice} />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          Original Invoice Without VAT{" "} {formData.original_currency_abbreviation ? `(${formData.original_currency_abbreviation})` : ""}
                        </label>
                        <NumericFormat
                          value={formData.invoice_without_vat}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          placeholder="0,00"
                          className="readonly-field"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Original Invoice With VAT{" "} {formData.original_currency_abbreviation ? `(${formData.original_currency_abbreviation})` : ""}</label>
                        <NumericFormat
                          value={formData.total_invoice_with_vat}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          placeholder="0,00"
                          className="readonly-field"
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Original System Without VAT{" "} {systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                        <NumericFormat
                          value={formData.invoice_without_vat_system}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          placeholder="0,00"
                          className="readonly-field"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Original System With VAT{" "} {systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                        <NumericFormat
                          value={formData.total_invoice_with_vat_system}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          placeholder="0,00"
                          className="readonly-field"
                          readOnly
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* SECTION 3: AMOUNTS */}
        <div className="AddInvoiceProcurement-section">
          <SectionHeader title="AMOUNTS" section="amounts" expanded={expandedSections.amounts} onToggle={toggleSection}/>

          {expandedSections.amounts && (
            <div className="section-content">
              {!isCreditNote ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Invoice Without VAT{" "} {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span className="required">*</span></label>
                      <NumericFormat
                        name="invoice_without_vat"
                        value={formData.invoice_without_vat}
                        onValueChange={({ floatValue }) =>
                          handleChange({target: { name: "invoice_without_vat", value: floatValue ?? "",},})
                        }
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="0,00"
                        className="AddInvoiceProcurement-input"
                      />
                      <ErrorMessage error={fieldErrors.invoice_without_vat} />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Supplier VAT (%) <span className="required">*</span></label>
                      <NumericFormat
                        name="supplier_vat"
                        value={formData.supplier_vat}
                        onValueChange={({ floatValue }) =>
                          handleChange({target: { name: "supplier_vat", value: floatValue ?? "" },})
                        }
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="21,00"
                        className="AddInvoiceProcurement-input"
                        thousandSeparator="."
                        decimalSeparator=","
                      />
                      <ErrorMessage error={fieldErrors.supplier_vat} />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Invoice With VAT{" "} {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span className="required">*</span></label>
                      <NumericFormat
                        value={formData.total_invoice_with_vat}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        placeholder="0,00"
                        className="readonly-field"
                        readOnly
                      />
                      <ErrorMessage error={fieldErrors.total_invoice_with_vat} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Total System Without VAT{" "} {systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                      <NumericFormat
                        value={formData.invoice_without_vat_system}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        placeholder="0,00"
                        className="readonly-field"
                        readOnly
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Total System With VAT{" "} {systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}
                      </label>
                      <NumericFormat
                        value={formData.total_invoice_with_vat_system}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        placeholder="0,00"
                        className="readonly-field"
                        readOnly
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Credit Note Without VAT{" "} {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span className="required">*</span>
                      </label>
                      <NumericFormat
                        name="credit_note_without_vat"
                        value={formData.credit_note_without_vat}
                        onValueChange={({ floatValue }) =>
                          handleChange({target: { name: "credit_note_without_vat", value: floatValue ?? "", },})
                        }
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="0,00"
                        className="AddInvoiceProcurement-input"
                      />
                      <ErrorMessage error={fieldErrors.credit_note_without_vat} />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Supplier VAT (%) <span className="required">*</span></label>
                      <NumericFormat
                        name="supplier_vat"
                        value={formData.supplier_vat}
                        onValueChange={({ floatValue }) =>
                          handleChange({target: { name: "supplier_vat", value: floatValue ?? "" },})
                        }
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="21,00"
                        className="AddInvoiceProcurement-input"
                        thousandSeparator="."
                        decimalSeparator=","
                      />
                      <ErrorMessage error={fieldErrors.supplier_vat} />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Credit Note With VAT{" "} {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span className="required">*</span></label>
                      <NumericFormat
                        value={formData.credit_note_with_vat}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        placeholder="0,00"
                        className="readonly-field"
                        readOnly
                      />
                      <ErrorMessage error={fieldErrors.credit_note_with_vat} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Credit Note System Without VAT{" "} {systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                      <NumericFormat
                        value={formData.credit_note_without_vat_system}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        placeholder="0,00"
                        className="readonly-field"
                        readOnly
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Credit Note System With VAT{" "} {systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                      <NumericFormat
                        value={formData.credit_note_with_vat_system}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        placeholder="0,00"
                        className="readonly-field"
                        readOnly
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* SECTION 4: DIFFERENCES */}
        {isCreditNote && (
          <div className="AddInvoiceProcurement-section">
            <SectionHeader title="DIFFERENCES" section="differences" expanded={expandedSections.differences} onToggle={toggleSection}/>

            {expandedSections.differences && (
              <div className="section-content">
                <div className="form-row">
                  <div className="form-group">
                    <label className="difference-label">Difference Without VAT{" "} {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}</label>
                    <NumericFormat
                      value={formData.difference_without_vat}
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      placeholder="0,00"
                      className={`readonly-field difference-field ${getDifferenceColorClass(formData.difference_without_vat)}`}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label className="difference-label">Difference With VAT{" "} {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}</label>
                    <NumericFormat
                      value={formData.difference_with_vat}
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      placeholder="0,00"
                      className={`readonly-field difference-field ${getDifferenceColorClass(formData.difference_with_vat)}`}
                      readOnly
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="difference-label">System Difference Without VAT{" "}{systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                    <NumericFormat
                      value={formData.difference_without_vat_system}
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      placeholder="0,00"
                      className={`readonly-field difference-field ${getDifferenceColorClass(formData.difference_without_vat_system)}`}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label className="difference-label">System Difference With VAT{" "}{systemCurrencyLabel ? `(${systemCurrencyLabel})` : ""}</label>
                    <NumericFormat
                      value={formData.difference_with_vat_system}
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      placeholder="0,00"
                      className={`readonly-field difference-field ${getDifferenceColorClass(formData.difference_with_vat_system)}`}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SECTION 5: ADDITIONAL DETAILS */}
        <div className="AddInvoiceProcurement-section">
          <SectionHeader title="ADDITIONAL DETAILS" section="additionalDetails" expanded={expandedSections.additionalDetails} onToggle={toggleSection}/>

          {expandedSections.additionalDetails && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Medium Alert (default 5 days){" "}<span className="required">*</span></label>
                  <input
                    type="number"
                    name="medium_alert"
                    value={formData.medium_alert}
                    onChange={handleChange}
                    className="AddInvoiceProcurement-input"
                  />
                  <ErrorMessage error={fieldErrors.medium_alert} />
                </div>

                <div className="form-group">
                  <label className="form-label">Critical Alert (default 1 day){" "} <span className="required">*</span></label>
                  <input
                    type="number"
                    name="critical_alert"
                    value={formData.critical_alert}
                    onChange={handleChange}
                    className="AddInvoiceProcurement-input"
                  />
                  <ErrorMessage error={fieldErrors.critical_alert} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Month <span className="required">*</span></label>
                  <input
                    type="text"
                    value={monthChoices.find((m) => m.value === formData.month)?.label || ""}
                    className="readonly-field"
                    readOnly
                  />
                  <ErrorMessage error={fieldErrors.month} />
                </div>

                <div className="form-group">
                  <label className="form-label">Year <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.year}
                    className="readonly-field"
                    readOnly
                  />
                  <ErrorMessage error={fieldErrors.year} />
                </div>
              </div>

              <div className="form-group full-width">
                <label className="form-label">Status</label>
                <input
                  type="text"
                  value="Unpaid"
                  className="readonly-field readonly-field--status"
                  readOnly
                />
              </div>

              <div className="form-group full-width">
                <label className="form-label">Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  className="AddInvoiceProcurement-description"
                  rows={4}
                  placeholder="Enter remarks..."
                />
              </div>
            </div>
          )}
        </div>

        {/* SECTION 6: ATTACHMENTS */}
        <div className="AddInvoiceProcurement-section">
          <SectionHeader title="ATTACHMENTS" section="attachments" expanded={expandedSections.attachments} onToggle={toggleSection}/>

          {expandedSections.attachments && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">Attachments</label>

                <div className="dropzone-wrapper">
                  <div key={dropzoneKey} className="dropzone" {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div className="dropzone-content">
                      {isDragActive ? (
                        <p>Drop the files here...</p>
                      ) : (
                        <p>Drag & drop files or click to select</p>
                      )}
                      <CloudUploadIcon style={{ fontSize: 40, color: "#888" }} />
                    </div>
                  </div>

                  {files.length > 0 && (
                    <div className="files-preview-container">
                      <h4>Selected Files ({files.length})</h4>
                      <div className="files-list">
                        {files.map((file, index) => (
                          <div key={index} className="file-preview-item">
                            <div className="file-info">
                              {getFileIcon(file)}
                              <span
                                className="file-name clickable"
                                title={file.type === "application/pdf" ? "Click to preview PDF" : "Click to download file"}
                                onClick={() => handlePreviewFile(file)}
                              >
                                {file.name.length > 40 ? `${file.name.substring(0, 40)}...` : file.name}
                              </span>
                              <span className="file-size">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-file-button"
                              onClick={() => handleRemoveFile(index)}
                              title="Remove file"
                            >
                              <DeleteIcon fontSize="small" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <p className="file-hint">
                  Accepted file types: PDF, Word, Excel, Images, Text. Multiple files allowed (max 100MB per file).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* BUTOANE */}
        <div className="form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate(-1)}
            disabled={isSaving}
          >
            Discard
          </button>

          <button type="submit" className="primary-btn" disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : ("Save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddInvoiceProcurement;